// Demo / preview injection — lets `?demo=storm` or `?demo=watch` exercise the full
// escalation against the user's real location with no live NWS event. Builds a
// realistic NwsAlert (polygon upstream of the user, motion toward them, IBW tags) so
// the screens are reviewable on the deployed site. Pure: takes the user's lat/lon.

import type { NwsAlert, LngLat } from '@/lib/nws/types'

export type DemoKind = 'storm' | 'watch' | 'tornado'

/** Read ?demo=… from a location.search string. Returns null when absent/unknown. */
export function demoKindFromSearch(search: string): DemoKind | null {
  const v = new URLSearchParams(search).get('demo')?.toLowerCase()
  return v === 'storm' || v === 'watch' || v === 'tornado' ? v : null
}

/**
 * A realistic, irregular warning polygon (lon/lat ring, closed) around a center —
 * an elongated NW→SE swath like a real NWS WarnGen polygon, NOT a tidy box. Offsets
 * are (dLat, dLon) in degrees.
 */
function warningSwath(lat: number, lon: number): LngLat[] {
  const verts: [number, number][] = [
    [0.4, -0.26],
    [0.24, 0.36],
    [-0.08, 0.48],
    [-0.38, 0.14],
    [-0.22, -0.36],
    [0.12, -0.42],
  ]
  const ring: LngLat[] = verts.map(([dLat, dLon]) => [lon + dLon, lat + dLat])
  ring.push(ring[0]) // close
  return ring
}

export function makeDemoAlert(kind: DemoKind, lat: number, lon: number): NwsAlert {
  const ends = new Date(Date.now() + 45 * 60_000).toISOString()
  const onset = new Date(Date.now() - 8 * 60_000).toISOString()

  // Cell sits to the WNW and moves ESE (toward the user) so the ETA reads "approaching".
  const cellLat = lat + 0.22
  const cellLon = lon - 0.38
  const motion = `${new Date().toISOString()}...storm...290DEG...38KT...${cellLat.toFixed(2)},${cellLon.toFixed(2)}`

  if (kind === 'watch') {
    return base({
      id: 'demo-svr-watch',
      event: 'Severe Thunderstorm Watch',
      severity: 'Severe',
      certainty: 'Possible',
      urgency: 'Future',
      ends,
      onset,
      geometry: null,
      headline: 'Severe Thunderstorm Watch in effect until this evening',
      description:
        'The Storm Prediction Center has issued a Severe Thunderstorm Watch for portions of your area. Damaging winds and large hail are the primary threats. Stay weather-aware and be ready to act if a warning is issued.',
      instruction: null,
      parameters: {},
    })
  }

  const tornado = kind === 'tornado'
  return base({
    id: tornado ? 'demo-tor-warning' : 'demo-svr-warning',
    event: tornado ? 'Tornado Warning' : 'Severe Thunderstorm Warning',
    severity: tornado ? 'Extreme' : 'Severe',
    certainty: 'Observed',
    urgency: 'Immediate',
    ends,
    onset,
    geometry: { type: 'Polygon', coordinates: [warningSwath(cellLat, cellLon)] },
    headline: tornado
      ? 'TORNADO EMERGENCY — take shelter now'
      : 'Severe Thunderstorm Warning for your area',
    description: tornado
      ? 'TORNADO EMERGENCY for the metro area. A confirmed large and destructive tornado was observed. Take cover now! Flying debris will be deadly to those caught without shelter.'
      : 'At the issued time, severe thunderstorms were located along a line moving east. Damaging winds and large hail are occurring with these storms.',
    instruction: tornado
      ? 'TAKE COVER NOW! Move to a basement or an interior room on the lowest floor of a sturdy building.'
      : 'For your protection move to an interior room on the lowest floor of a building.',
    parameters: tornado
      ? {
          eventMotionDescription: [motion],
          tornadoDetection: ['OBSERVED'],
          maxWindGust: ['80 MPH'],
          maxHailSize: ['1.50'],
          tornadoDamageThreat: ['CATASTROPHIC'],
        }
      : {
          eventMotionDescription: [motion],
          windThreat: ['OBSERVED'],
          maxWindGust: ['70 MPH'],
          hailThreat: ['RADAR INDICATED'],
          maxHailSize: ['1.00'],
          thunderstormDamageThreat: ['CONSIDERABLE'],
        },
  })
}

function base(p: Partial<NwsAlert> & Pick<NwsAlert, 'id' | 'event'>): NwsAlert {
  return {
    severity: 'Severe',
    certainty: 'Observed',
    urgency: 'Immediate',
    messageType: 'Alert',
    headline: null,
    description: '',
    instruction: null,
    onset: null,
    ends: null,
    expires: p.ends ?? null,
    effective: null,
    areaDesc: 'Your area (demo)',
    senderName: 'NWS (demo)',
    category: 'Met',
    response: 'Shelter',
    geometry: null,
    parameters: {},
    ...p,
  }
}
